import BackButton from "../components/BackButton";
import { formatFranc } from '../lib/utils';
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Bell, Zap, Trophy, ArrowRightLeft, Crown, Clock } from 'lucide-react';
import FrancIcon from '../components/FrancIcon';

export default function NotificationsPanel({ onBack, team }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load recent scoring events and transactions as notifications
    const load = async () => {
      try {
        const newsRes = await api.get('/news', { params: { limit: 10 } });

        const notifs = [];
        const news = newsRes.data.news || [];
        
        // Generate notifications from news
        news.forEach(item => {
          const points = Object.values(item.scored_points || {}).reduce((a, b) => a + b, 0);
          if (points > 0) {
            notifs.push({
              id: item.id,
              type: 'scoring',
              icon: Zap,
              title: item.headline,
              subtitle: `${item.entity_names?.join(', ')} earned ${formatFranc(points, { sign: true })}`,
              time: item.timestamp,
              color: 'text-[hsl(43,74%,52%)]',
            });
          }
        });
        
        // Team status notification — 2026-06 league audit: use the
        // header-selected league's team (prop), not blind teams[0].
        const t = team;
        if (t?.slots?.length > 0) {
            notifs.unshift({
              id: 'team-status',
              type: 'info',
              icon: Trophy,
              title: `${t.name}: ${formatFranc(t.total_points)} earned`,
              subtitle: `${t.slots.length} people • ${formatFranc(t.budget_remaining)} remaining`,
              time: new Date().toISOString(),
              color: 'text-primary',
            });
        }

        setNotifications(notifs);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    // `team` is league-scoping relevant: re-run if the selected team changes
    // while the panel stays mounted in the nav stack.
  }, [team]);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="notifications-panel">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Bell className="h-5 w-5 inline mr-2" />
        Notifications
      </h2>

      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="space-y-2">
          {notifications.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No notifications yet. Draft people and events will appear here.
            </div>
          )}
          {notifications.map((notif) => {
            const Icon = notif.icon;
            return (
              <div
                key={notif.id}
                className="flex items-start gap-3 bg-card border border-border/60 rounded-2xl px-4 py-3"
              >
                <div className={`mt-0.5 ${notif.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{notif.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{notif.subtitle}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />
                    {notif.time ? new Date(notif.time).toLocaleString() : 'Just now'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
